import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  createAssessmentType,
  getAssessmentTypesForSettings,
  updateAssessmentType,
} from "../api/assessmentApi";
import {
  createComplianceType,
  getComplianceTypesForSettings,
  updateComplianceTypeSetting,
} from "../api/complianceApi";

const emptyForm = {
  id: null,
  name: "",
  retired: false,
  requiresFireDoorChecklist: false,
  confirmRenameImpact: false,
};

function TypeManagerCard({
  title,
  description,
  items,
  loading,
  onCreate,
  onUpdate,
  supportsFireDoorChecklist = false,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formValues, setFormValues] = useState(emptyForm);
  const [originalName, setOriginalName] = useState("");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const isEdit = Boolean(formValues.id);
  const trimmedName = formValues.name.trim();
  const isRename = isEdit && trimmedName !== originalName;
  const renameBlocked = isRename && !formValues.confirmRenameImpact;

  const sortedItems = useMemo(
    () => [...items].sort((left, right) => {
      if (left.retired !== right.retired) {
        return left.retired ? 1 : -1;
      }
      return left.name.localeCompare(right.name);
    }),
    [items]
  );

  const openCreate = () => {
    setError("");
    setOriginalName("");
    setFormValues(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setError("");
    setOriginalName(item.name || "");
    setFormValues({
      id: item.id,
      name: item.name || "",
      retired: Boolean(item.retired),
      requiresFireDoorChecklist: Boolean(item.requires_fire_door_checklist),
      confirmRenameImpact: false,
    });
    setDialogOpen(true);
  };

  const closeDialog = (force = false) => {
    if (saving && !force) return;
    setDialogOpen(false);
    setError("");
    setOriginalName("");
    setFormValues(emptyForm);
  };

  const handleSubmit = async () => {
    if (!trimmedName) {
      setError("Name is required.");
      return;
    }
    if (renameBlocked) {
      setError("You must confirm the rename warning before saving.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        name: trimmedName,
        retired: formValues.retired,
        requires_fire_door_checklist: supportsFireDoorChecklist
          ? formValues.requiresFireDoorChecklist
          : false,
        confirm_rename_impact: formValues.confirmRenameImpact,
      };
      if (isEdit) {
        await onUpdate(formValues.id, payload);
        setToast({
          open: true,
          message: `${title.slice(0, -1)} updated.`,
          severity: "success",
        });
      } else {
        await onCreate(payload);
        setToast({
          open: true,
          message: `${title.slice(0, -1)} created.`,
          severity: "success",
        });
      }
      closeDialog(true);
    } catch (saveError) {
      setError(saveError?.response?.data?.error || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleRetireToggle = async (item, retired) => {
    try {
      await onUpdate(item.id, {
        name: item.name,
        retired,
        requires_fire_door_checklist: Boolean(item.requires_fire_door_checklist),
        confirm_rename_impact: false,
      });
      setToast({
        open: true,
        message: retired ? `${title.slice(0, -1)} retired.` : `${title.slice(0, -1)} reactivated.`,
        severity: "success",
      });
    } catch (updateError) {
      setToast({
        open: true,
        message: updateError?.response?.data?.error || "Failed to update.",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
          <Button variant="contained" onClick={openCreate}>
            Add
          </Button>
        </Box>

        <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  {supportsFireDoorChecklist && <TableCell>Fire Door Checklist</TableCell>}
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={supportsFireDoorChecklist ? 4 : 3} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      Loading...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : !sortedItems.length ? (
                <TableRow>
                  <TableCell colSpan={supportsFireDoorChecklist ? 4 : 3} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No items found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.name}</TableCell>
                    {supportsFireDoorChecklist && (
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.requires_fire_door_checklist ? "Required" : "Not required"}
                          color={item.requires_fire_door_checklist ? "info" : "default"}
                          variant={item.requires_fire_door_checklist ? "filled" : "outlined"}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.retired ? "Retired" : "Active"}
                        color={item.retired ? "default" : "success"}
                        variant={item.retired ? "outlined" : "filled"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => openEdit(item)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color={item.retired ? "success" : "warning"}
                          onClick={() => handleRetireToggle(item, !item.retired)}
                        >
                          {item.retired ? "Reactivate" : "Retire"}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{isEdit ? `Edit ${title.slice(0, -1)}` : `Add ${title.slice(0, -1)}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Name"
              value={formValues.name}
              onChange={(event) =>
                setFormValues((prev) => ({
                  ...prev,
                  name: event.target.value,
                  confirmRenameImpact: isEdit ? prev.confirmRenameImpact : false,
                }))
              }
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.retired}
                  onChange={(event) =>
                    setFormValues((prev) => ({ ...prev, retired: event.target.checked }))
                  }
                />
              }
              label="Retired"
            />
            {supportsFireDoorChecklist && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.requiresFireDoorChecklist}
                    onChange={(event) =>
                      setFormValues((prev) => ({
                        ...prev,
                        requiresFireDoorChecklist: event.target.checked,
                      }))
                    }
                  />
                }
                label="Requires fire door checklist"
              />
            )}
            {isRename && (
              <Alert severity="warning">
                Renaming this item might affect existing records that rely on this type.
              </Alert>
            )}
            {isRename && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.confirmRenameImpact}
                    onChange={(event) =>
                      setFormValues((prev) => ({
                        ...prev,
                        confirmRenameImpact: event.target.checked,
                      }))
                    }
                  />
                }
                label="I understand renaming this item might affect existing records that rely on it."
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default function TypesListsSettingsPage() {
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [complianceTypes, setComplianceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const [assessmentData, complianceData] = await Promise.all([
        getAssessmentTypesForSettings(),
        getComplianceTypesForSettings(),
      ]);
      setAssessmentTypes(assessmentData);
      setComplianceTypes(complianceData);
    } catch (loadError) {
      console.error("Failed to load type settings:", loadError);
      setError("Failed to load types and lists settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAssessmentCreate = async (payload) => {
    const response = await createAssessmentType(payload);
    await loadData();
    return response;
  };

  const handleAssessmentUpdate = async (typeId, payload) => {
    const response = await updateAssessmentType(typeId, payload);
    await loadData();
    return response;
  };

  const handleComplianceCreate = async (payload) => {
    const response = await createComplianceType(payload);
    await loadData();
    return response;
  };

  const handleComplianceUpdate = async (typeId, payload) => {
    const response = await updateComplianceTypeSetting(typeId, payload);
    await loadData();
    return response;
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" fontWeight={600}>
          Types & Lists
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage configurable system type lists. Retired items stay on existing records but are hidden from normal selection lists.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <TypeManagerCard
        title="Assessment Types"
        description="Used when creating and editing assessments."
        items={assessmentTypes}
        loading={loading}
        onCreate={handleAssessmentCreate}
        onUpdate={handleAssessmentUpdate}
      />

      <TypeManagerCard
        title="Compliance Types"
        description="Used when creating schedules and compliance tasks."
        items={complianceTypes}
        loading={loading}
        onCreate={handleComplianceCreate}
        onUpdate={handleComplianceUpdate}
        supportsFireDoorChecklist
      />
    </Stack>
  );
}
